import * as Sentry from '@sentry/react'
import type { UseStore } from 'idb-keyval'
import { createStore, del, get, set, clear, keys } from 'idb-keyval'
import type { BlastSource } from '../../Model/Project/BlastSource.js'
import type { SpinSelection } from './CubeKey.js'
import { cubeKey } from './CubeKey.js'
import { recordCubeDiag } from './CubeDiagnostics.js'

/**
 * Bounded persistent cache of visualization cubes, keyed by `(scopeId, blastType[, spin])`.
 *
 * Stores the raw Arrow IPC bytes (not the decoded `Table`): Arrow Tables hold native
 * heap buffers that don't serialize cleanly through structured-clone, but the IPC byte
 * stream is the canonical serialization and survives a `Uint8Array` round-trip via
 * IndexedDB natively. Decoding back to a `Table` happens at read time in the slice/epic
 * via `tableFromIPC(bytes)`.
 *
 * Eviction: LRU bounded by bytes. The index records each entry's size, most recent first; on
 * `putCachedCube` the touched key moves to the front and least-recently-used entries are deleted until
 * the total fits [CUBE_CACHE_BUDGET_BYTES] (capped at half the browser's storage quota) and the count
 * fits [MAX_ENTRIES]. The entry just written is never evicted, so a cube larger than the whole budget is
 * still stored, alone.
 *
 * Quota failures are caught and reported as a Sentry breadcrumb. The cube path then
 * proceeds without persistence — every fetch is a fresh round-trip until the user clears
 * browser storage. We deliberately don't surface this to the workspace UI; the cube still
 * works, just slower across reloads.
 *
 */

export type CachedCube = {
  bytes: Uint8Array
  etag: string | null
  loadedAt: number
}

export const CUBE_CACHE_BUDGET_BYTES = 256 * 1024 * 1024
/** Backstop so the index cannot grow without bound when cubes are tiny. */
export const MAX_ENTRIES = 64
const QUOTA_FRACTION = 0.5
const INDEX_KEY = '__lru_order__'
const INDEX_VERSION = 2

type IndexEntry = { key: string; bytes: number }
type IndexV2 = { version: typeof INDEX_VERSION; entries: IndexEntry[] }

const isIndexEntry = (e: unknown): e is IndexEntry =>
  typeof (e as IndexEntry)?.key === 'string' && Number.isFinite((e as IndexEntry).bytes)

const isIndexV2 = (v: unknown): v is IndexV2 =>
  (v as IndexV2)?.version === INDEX_VERSION &&
  Array.isArray((v as IndexV2).entries) &&
  (v as IndexV2).entries.every(isIndexEntry)

/** Heuristic: an IndexedDB write failure whose error looks like a storage-quota rejection. */
const isQuotaError = (e: unknown): boolean => {
  const name = (e as { name?: string })?.name ?? ''
  const msg = String(e)
  return /quota/i.test(name) || /quota/i.test(msg) || name === 'QuotaExceededError'
}

/**
 * `createStore` calls `indexedDB.open(...)` synchronously — fine in the browser but breaks in
 * jsdom (vitest) where `indexedDB` is undefined. Lazily build the store on first access so
 * test environments that import this module without exercising the cache don't crash.
 */
let _store: UseStore | undefined
const store = (): UseStore => {
  if (!_store) _store = createStore('spcfy-viz-cube', 'cubes')
  return _store
}

const cacheKey = (scopeId: string, blastType: BlastSource, spin?: SpinSelection): string =>
  cubeKey(scopeId, blastType, spin)

/** [CUBE_CACHE_BUDGET_BYTES], lowered to half the storage quota when the browser reports one. */
const effectiveBudget = async (): Promise<number> => {
  try {
    const quota = (await navigator.storage?.estimate?.())?.quota
    if (typeof quota === 'number' && quota > 0) {
      return Math.min(CUBE_CACHE_BUDGET_BYTES, Math.floor(quota * QUOTA_FRACTION))
    }
  } catch {
    /* estimate unavailable or rejected: use the default */
  }
  return CUBE_CACHE_BUDGET_BYTES
}

const readIndex = async (): Promise<IndexEntry[]> => {
  try {
    const raw = await get<unknown>(INDEX_KEY, store())
    if (raw === undefined) return []
    if (isIndexV2(raw)) return raw.entries
    // The count-based index had no sizes (a bare string[]) and can't be trusted to describe the store.
    // Its entries are at most a handful of cubes that refill on demand, so drop them rather than
    // re-reading each one to learn its size; dropping only the index would orphan the bytes.
    await clear(store())
    return []
  } catch (e) {
    Sentry.addBreadcrumb({
      category: 'viz.cubeCache',
      level: 'warning',
      message: 'failed to read LRU index',
      data: { error: String(e) },
    })
    return []
  }
}

const writeIndex = async (entries: IndexEntry[]): Promise<void> => {
  const index: IndexV2 = { version: INDEX_VERSION, entries }
  await set(INDEX_KEY, index, store())
}

export const getCachedCube = async (
  scopeId: string,
  blastType: BlastSource,
  spin?: SpinSelection,
): Promise<CachedCube | undefined> => getCachedCubeByKey(cacheKey(scopeId, blastType, spin))

/** {@link getCachedCube} for a cube whose identity is not a {@link cubeKey}, such as the OTU table's. */
export const getCachedCubeByKey = async (key: string): Promise<CachedCube | undefined> => {
  try {
    const cached = await get<CachedCube>(key, store())
    if (cached) {
      const entries = await readIndex()
      await writeIndex([{ key, bytes: cached.bytes.byteLength }, ...entries.filter((e) => e.key !== key)])
    }
    return cached
  } catch (e) {
    Sentry.addBreadcrumb({
      category: 'viz.cubeCache',
      level: 'warning',
      message: 'failed to read cube cache',
      data: { key, error: String(e) },
    })
    recordCubeDiag({ kind: 'idb-error', key, detail: `read failed: ${String(e)}` })
    return undefined
  }
}

export const putCachedCube = async (
  scopeId: string,
  blastType: BlastSource,
  cube: CachedCube,
  spin?: SpinSelection,
): Promise<void> => putCachedCubeByKey(cacheKey(scopeId, blastType, spin), cube)

export const putCachedCubeByKey = async (key: string, cube: CachedCube): Promise<void> => {
  try {
    const entries = await readIndex()
    const next = [{ key, bytes: cube.bytes.byteLength }, ...entries.filter((e) => e.key !== key)]
    const budget = await effectiveBudget()

    let total = next.reduce((sum, e) => sum + e.bytes, 0)
    let keep = next.length
    while (keep > 1 && (total > budget || keep > MAX_ENTRIES)) {
      keep--
      total -= next[keep].bytes
    }
    const kept = next.slice(0, keep)
    const evicted = next.slice(keep)

    // Delete evicted bytes and write the index *before* storing the new cube.
    // Invariant: the index stays a superset of the keys present in the store. A
    // dangling index entry (key tracked, bytes missing) is harmless — reads
    // tolerate it and it's evicted on the next pass. The reverse — bytes present
    // but absent from the index — is an unevictable leak (eviction only ever
    // walks the index), so we never open that window: if a write below throws,
    // we're left with at most a dangling entry, never an orphaned cube.
    if (evicted.length) {
      await Promise.all(evicted.map((e) => del(e.key, store())))
      for (const e of evicted) {
        recordCubeDiag({
          kind: 'idb-evict',
          key: e.key,
          bytes: e.bytes,
          detail: `over budget=${budget} bytes or MAX_ENTRIES=${MAX_ENTRIES}`,
        })
      }
    }

    await writeIndex(kept)
    await set(key, cube, store())
  } catch (e) {
    Sentry.addBreadcrumb({
      category: 'viz.cubeCache',
      level: 'warning',
      message: 'failed to persist cube (quota or IndexedDB error)',
      data: { key, bytes: cube.bytes.byteLength, error: String(e) },
    })
    recordCubeDiag({
      kind: isQuotaError(e) ? 'idb-quota' : 'idb-error',
      key,
      bytes: cube.bytes.byteLength,
      detail: `persist failed: ${String(e)}`,
    })
  }
}

export const clearCubeCache = async (): Promise<void> => {
  try {
    await clear(store())
  } catch (e) {
    Sentry.addBreadcrumb({
      category: 'viz.cubeCache',
      level: 'warning',
      message: 'failed to clear cube cache',
      data: { error: String(e) },
    })
  }
}

/** For tests/diagnostics. Returns persisted cube keys in LRU order (most recent first). */
export const debugListCachedCubes = async (): Promise<string[]> => {
  const order = (await readIndex()).map((e) => e.key)
  const present = (await keys(store())).map(String).filter((k) => k !== INDEX_KEY)
  // Return index order, but only keys that still exist in the store
  const presentSet = new Set(present)
  return order.filter((k) => presentSet.has(k))
}

/** Per-cube cached entry summary (byte size + etag + age), LRU order. Drives the diagnostics widget. */
export interface CachedCubeSummary {
  key: string
  bytes: number
  etag: string | null
  loadedAt: number
}

export const getCachedCubeSummaries = async (): Promise<CachedCubeSummary[]> => {
  const out: CachedCubeSummary[] = []
  try {
    for (const key of await debugListCachedCubes()) {
      const entry = await get<CachedCube>(key, store())
      if (entry) out.push({ key, bytes: entry.bytes.byteLength, etag: entry.etag, loadedAt: entry.loadedAt })
    }
  } catch {
    /* best-effort; the widget tolerates an empty list */
  }
  return out
}

export interface CubeCacheStats {
  entries: number
  bytes: number
  budget: number
}

/** Index-tracked entry count and total bytes against the effective budget. For debugging; no UI. */
export const cubeCacheStats = async (): Promise<CubeCacheStats> => {
  const entries = await readIndex()
  return { entries: entries.length, bytes: entries.reduce((sum, e) => sum + e.bytes, 0), budget: await effectiveBudget() }
}
